import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FeatureCard } from '@/components/ui/glass-card';
import { Compass, Workflow, FileSearch, Layers, Zap, Shield } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: 'Enterprise data protection',
    description:
      'UK-hosted infrastructure and EU-based AI processing, with GDPR compliance. Your statements are not used to train AI models.',
  },
  {
    icon: Compass,
    title: 'Statement concierge',
    description:
      'Guided intake with precise validation so every document arrives in optimal condition for review.',
  },
  {
    icon: FileSearch,
    title: 'Criterion-by-criterion scoring',
    description:
      'Each statement is scored against 48 criteria, or 61 in the advanced framework, which adds the TISC and International Reporting Template criteria.',
  },
  {
    icon: Workflow,
    title: 'Risk-mapped recommendations',
    description:
      'Priority actions grouped by immediacy and resourcing, ready to flow into your compliance programme.',
  },
  {
    icon: Layers,
    title: 'Benchmark intelligence',
    description:
      'See how you rank against peers across disclosure, due diligence, and prevention activities.',
  },
  {
    icon: Zap,
    title: 'Bulk analysis',
    description:
      'Reviewing many statements, for a portfolio or a research project? Contact us about bulk analysis.',
  },
];

export function Features() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="w-full bg-white py-24">
      <div className="mx-auto flex max-w-6xl flex-col gap-16 px-6">
        <div className="max-w-2xl space-y-4">
          <p className="eyebrow">
            Designed for precision
          </p>
          <h2 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)] md:text-4xl">
            A considered workflow that mirrors the standards of your compliance team.
          </h2>
          <p className="text-lg leading-relaxed text-[var(--text-secondary)]">
            From the first upload to the final report, every surface is engineered to remove noise and elevate the
            insight that moves programmes forward.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.21, 0.47, 0.32, 0.98]
              }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <FeatureCard {...feature} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
